import React, { useState } from 'react';
import { Flag } from 'lucide-react';
import { Button } from '../ui/Button';
import { ConfirmationModal } from '../ui/ConfirmationModal';
import { cn } from '../../utils/cn';

interface GiveUpButtonProps {
  onGiveUp: () => void;
  disabled?: boolean;
  className?: string;
}

/**
 * GiveUpButton component - allows players to forfeit the current game
 * Shows a confirmation modal before submitting the give-up action
 */
export const GiveUpButton: React.FC<GiveUpButtonProps> = ({
  onGiveUp,
  disabled = false,
  className
}) => {
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleGiveUpClick = () => {
    setShowConfirmation(true);
  };

  const handleConfirm = () => {
    setShowConfirmation(false);
    onGiveUp();
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={handleGiveUpClick}
        disabled={disabled}
        className={cn(
          'w-full mobile-touch-target',
          'border-red-300 dark:border-red-700',
          'text-red-600 dark:text-red-400',
          'hover:bg-red-50 dark:hover:bg-red-950/20',
          'hover:border-red-400 dark:hover:border-red-600',
          'transition-colors duration-200 ease-theme',
          className
        )}
      >
        <Flag className="w-4 h-4 mr-2" />
        Give Up
      </Button>

      <ConfirmationModal
        isOpen={showConfirmation}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title="Give Up?"
        description="Are you sure you want to give up? This will count as a loss with 4 attempts and won't count toward your streak."
        confirmText="Yes, Give Up"
        cancelText="Keep Playing"
        variant="danger"
        requirePassword={false}
      />
    </>
  );
};
